import React, { useEffect, useState, useMemo } from 'react';
import { ReactComponent as SVGLogo } from '../../../../images/icons/logo.svg';
import EditIcon from '../../../../images/icons/pencil.svg';
import './MasterUserDashboard.scss';
import BreadCrumbs from '../../../../components/BreadCrumbs/BreadCrumbs';
import { t } from 'i18next';
import { UserService, MasterUserDto } from '../../../../services/user.service';
import { useService } from 'react-service-locator';
import { useHistory } from 'react-router-dom';

const PAGE_SIZE = 15;

const MasterUserDashboard: React.FC = () => {
    const userService = useService(UserService);
    const history = useHistory();
    const [users, setUsers] = useState<MasterUserDto[]>([]);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);

    const isDemo = (licenseType: number | string) => licenseType === 2 || licenseType === 'Demo';

    useEffect(() => {
        userService.fetchAllUsers().then((list) => {
            setUsers(list || []);
        });
    }, [userService]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        if (!q) return users;
        return users.filter(
            (u) =>
                u.email?.toLowerCase().includes(q) ||
                u.firstName?.toLowerCase().includes(q) ||
                u.lastName?.toLowerCase().includes(q),
        );
    }, [users, search]);

    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageItems = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
        setPage(0);
    };

    return (
        <div id="settingsPage" className="settings">
            <BreadCrumbs
                breadcrumbs={[
                    { to: '/', name: t('search') },
                    { to: '/admin-settings', name: 'Settings' },
                    { to: '/master-dashboard', name: 'Dashboard' },
                ]}
            />
            <div className="card">
                <div className="title">
                    <SVGLogo height={50} width="auto" />
                </div>

                <div className="toolbar">
                    <input
                        className="search-input"
                        type="text"
                        placeholder="Search by name or email…"
                        value={search}
                        onChange={handleSearch}
                    />
                    <span className="user-count">
                        {filtered.length} user{filtered.length !== 1 ? 's' : ''}
                    </span>
                </div>

                <div className="table-wrapper">
                    <table className="user-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>License</th>
                                <th>Expires</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pageItems.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="empty-row">
                                        No users found
                                    </td>
                                </tr>
                            ) : (
                                pageItems.map((u) => (
                                    <tr key={u.id}>
                                        <td className="name-cell">
                                            {[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}
                                        </td>
                                        <td className="email-cell">{u.email}</td>
                                        <td>{u.phoneNumber || '—'}</td>
                                        <td>
                                            <span
                                                className={`license-badge ${isDemo(u.licenseType) ? 'demo' : 'paid'}`}
                                            >
                                                {u.licenseType}
                                            </span>
                                        </td>
                                        <td>{u.licenseEnd ? new Date(u.licenseEnd).toLocaleDateString() : '—'}</td>
                                        <td>
                                            {isDemo(u.licenseType) ? (
                                                <button
                                                    type="button"
                                                    className="edit-action-button"
                                                    onClick={() =>
                                                        history.push(`/admin-settings/dashboard/demo/${u.id}/edit`)
                                                    }
                                                >
                                                    <img src={EditIcon} alt="Edit" />
                                                </button>
                                            ) : (
                                                '—'
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="pagination">
                        <button className="page-btn" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                            ‹
                        </button>
                        <span className="page-info">
                            Page {page + 1} of {totalPages}
                        </span>
                        <button
                            className="page-btn"
                            disabled={page >= totalPages - 1}
                            onClick={() => setPage((p) => p + 1)}
                        >
                            ›
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MasterUserDashboard;
